import { createRouter, createWebHistory } from 'vue-router';
import HomeView from '../views/HomeView.vue';
import OrderNow from '../views/OrderNow.vue';
import BoxLanding from '@/views/BoxLanding.vue';
import CheckoutPage from '@/views/CheckoutPage.vue';
import OurStory from '@/views/OurStory.vue';
import ProfilePage from '@/views/ProfilePage.vue';
import ContactUs from '@/views/ContactUs.vue';
import OrderSuccess from '@/views/OrderSuccess.vue';
import SurveyPage from '@/views/SurveyPage.vue';
import PayBalance from '@/views/PayBalance.vue';
const DashboardLayout = () => import('@/views/dashboard/DashboardLayout.vue');
const Overview = () => import('@/views/dashboard/Overview.vue');
const TodayBoard = () => import('@/views/dashboard/TodayBoard.vue');
const NewOrder = () => import('@/views/dashboard/NewOrder.vue');
const Surveys = () => import('@/views/dashboard/Surveys.vue');
const OrdersView = () => import('@/views/dashboard/Orders.vue');
const ProductsView = () => import('@/views/dashboard/Products.vue');
const ProductEditor = () => import('@/views/dashboard/ProductEditor.vue');
const KitchenView = () => import('@/views/dashboard/Kitchen.vue');
const AnalyticsView = () => import('@/views/dashboard/Analytics.vue');
const TrafficView = () => import('@/views/dashboard/Traffic.vue');
const IntegrationsView = () => import('@/views/dashboard/Integrations.vue');
const SettingsView = () => import('@/views/dashboard/Settings.vue');
const DeliveriesView = () => import('@/views/dashboard/Deliveries.vue');
const CustomersView = () => import('@/views/dashboard/Customers.vue');
const AcquisitionView = () => import('@/views/dashboard/Acquisition.vue');
const RetentionView = () => import('@/views/dashboard/Retention.vue');
const WinbackView = () => import('@/views/dashboard/Winback.vue');
const InvoicesView = () => import('@/views/dashboard/Invoices.vue');
const MarketingView = () => import('@/views/dashboard/Marketing.vue');
const DispatchView = () => import('@/views/dashboard/Dispatch.vue');
const SmsCentre = () => import('@/views/dashboard/SmsCentre.vue');
const CircleAdmin = () => import('@/views/dashboard/CircleAdmin.vue');
// CHDS Circle — public pages, deliberately NOT linked from the storefront nav or
// footer yet (owner's call: live but hidden until launch).
const CircleLanding = () => import('@/views/circle/CircleLanding.vue');
const CircleJoin = () => import('@/views/circle/CircleJoin.vue');
const CircleDashboard = () => import('@/views/circle/CircleDashboard.vue');
const CircleProfile = () => import('@/views/circle/CircleProfile.vue');
const CircleLegal = () => import('@/views/circle/CircleLegal.vue');
const KnowledgeBase = () => import('@/views/dashboard/KnowledgeBase.vue');
const FinanceView = () => import('@/views/dashboard/Finance.vue');
const WalletsView = () => import('@/views/dashboard/Wallets.vue');
const SupportView = () => import('@/views/dashboard/Support.vue');
const TranslationsView = () => import('@/views/dashboard/Translations.vue');
const OpsSettings = () => import('@/views/dashboard/OpsSettings.vue');
const ChatbotManager = () => import('@/views/dashboard/ChatbotManager.vue');
const FeedbackView = () => import('@/views/dashboard/Feedback.vue');
const ActivityView = () => import('@/views/dashboard/Activity.vue');
const AutomationsView = () => import('@/views/dashboard/Automations.vue');
const MyNotificationsView = () => import('@/views/dashboard/MyNotifications.vue');
const SeoManager = () => import('@/views/dashboard/SeoManager.vue');
const ParserCatalogue = () => import('@/views/dashboard/ParserCatalogue.vue');
const BoxManager = () => import('@/views/dashboard/BoxManager.vue');
const DishInfoLibrary = () => import('@/views/dashboard/DishInfoLibrary.vue');
import { useAuthStore } from '@/stores/auth';
import { useWeeklyMenuStore } from '@/stores/weeklyMenu';
import { useTranslationStore } from '@/stores/translation';

/** The Pinia store is empty on a cold page load (a bookmark, or F5) — the user is
 *  only in localStorage until initializeAuth() runs. Reading the store alone sent a
 *  driver to Overview, which 403s, which logged them out. */
function currentRole() {
  const store = useAuthStore();
  if (store.user?.role) return store.user.role;
  try { return (JSON.parse(localStorage.getItem('user')) || {}).role || null; } catch (e) { return null; }
}

/** Routes each restricted role may open. Anything else redirects to their home
 *  screen instead of 403-ing and bouncing them to the login page. */
const ROLE_HOME = { driver: 'dash-deliveries' };
const ROLE_ALLOWED = {
  driver: ['dash-deliveries'],
};

const routes = [
  {
    path: '/',
    name: 'Home',
    component: HomeView,
    meta: {
      title: 'Chi Hun Da Su',
      favicon: '@/assets/icons/favicon.ico',
    },
  },

  {
    path: '/ordernow',
    name: 'Ordernow',
    component: OrderNow,
    meta: {
      title: 'Order Now - Chi Hun Da Su',
      favicon: '@/assets/icons/favicon.ico',
    },
  },

  {
    path: '/box',
    name: 'Box',
    component: BoxLanding,
    meta: { title: 'Your CHDS box — fresh, healthy meals delivered in Melbourne' },
  },

  {
    path: '/checkout',
    name: 'CheckoutPage',
    component: CheckoutPage,
    meta: {
      title: 'Check Out - Chi Hun Da Su',
      favicon: '@/assets/icons/favicon.ico',
    },
  },
  
  {
    path: '/ourstory',
    name: 'Ourstory',
    component: OurStory,
    meta: {
      title: 'Our Story - Chi Hun Da Su',
      favicon: '@/assets/icons/favicon.ico',
    },
  },

  {
    path: '/profile',
    name: 'profilepage',
    component: ProfilePage,
    meta: {
      title: 'Profile Page - Chi Hun Da Su',
      favicon: '@/assets/icons/favicon.ico',
    },
  },

  {
    path: '/survey/:token',
    name: 'survey',
    component: SurveyPage,
    meta: { title: 'CHDS — Your feedback' },
  },
  {
    path: '/pay/:token',
    name: 'pay-balance',
    component: PayBalance,
    meta: { title: 'Pay your CHDS balance' },
  },
  {
    path: '/order/success',
    name: 'ordersuccess',
    component: OrderSuccess,
    meta: {
      title: 'Order Success - Chi Hun Da Su',
      favicon: '@/assets/icons/favicon.ico',
    },
  },

  {
    path: '/contact-us',
    name: 'contactus',
    component: ContactUs,
    meta: {
      title: 'Contact Us - Chi Hun Da Su',
      favicon: '@/assets/icons/favicon.ico',
    },
  },

  {
    path: '/privacy',
    name: 'privacy',
    component: () => import('@/views/legal/PrivacyPolicy.vue'),
    meta: { title: 'Privacy Policy - CHDS' },
  },
  {
    path: '/terms',
    name: 'terms',
    component: () => import('@/views/legal/TermsConditions.vue'),
    meta: { title: 'Terms & Conditions - CHDS' },
  },
  {
    path: '/refund-policy',
    name: 'refund-policy',
    component: () => import('@/views/legal/RefundPolicy.vue'),
    meta: { title: 'Refunds & Returns - CHDS' },
  },

  // ---- CHDS Circle (public; affiliate auth is its own token, not the staff guard) ----
  { path: '/menu', name: 'weekly-menu', component: () => import('@/views/WeeklyMenu.vue'), meta: { title: 'This week\'s menu — CHDS | 本周菜单' } },
  { path: '/packages', name: 'packages', component: () => import('@/views/Packages.vue'), meta: { title: 'Meal packages — CHDS | 餐食套餐' } },
  { path: '/checkout-week', name: 'weekly-checkout', component: () => import('@/views/WeeklyCheckout.vue'), meta: { title: 'Checkout — CHDS' } },
  { path: '/order/group/:id', name: 'weekly-order-success', component: () => import('@/views/WeeklyOrderSuccess.vue'), meta: { title: 'Order confirmed — CHDS' } },
  { path: '/dish/:slug', name: 'dish', component: () => import('@/views/DishPage.vue'), meta: { title: 'Dish — CHDS' } },
  { path: '/guides', name: 'guides', component: () => import('@/views/GuidesIndex.vue'), meta: { title: 'Guides — CHDS' } },
  { path: '/guides/:gslug', name: 'guide', component: () => import('@/views/GuidePage.vue'), meta: { title: 'Guide — CHDS' } },
  { path: '/my-subscriptions', name: 'my-subscriptions', component: () => import('@/views/MySubscriptions.vue'), meta: { title: 'My subscriptions — CHDS | 我的订阅' } },
  { path: '/s/:token', name: 'subscription-pick', component: () => import('@/views/SubscriptionPick.vue'), meta: { title: 'Choose your dishes — CHDS | 选菜' } },
  {
    path: '/find-my-meals',
    name: 'find-my-meals',
    component: () => import('@/views/FindMyMeals.vue'),
    meta: { title: 'Find My Meals — CHDS | 为我配餐' },
  },
  {
    path: '/m/:slug',
    name: 'find-my-meals-short',
    component: () => import('@/views/FindMyMeals.vue'),
    meta: { title: 'Find My Meals — CHDS | 为我配餐' },
  },
  { path: '/circle', name: 'circle-landing', component: CircleLanding, meta: { title: 'CHDS Circle — get paid to share 吃货圈' } },
  { path: '/circle/join', name: 'circle-join', component: CircleJoin, meta: { title: 'Join CHDS Circle' } },
  { path: '/circle/dashboard', name: 'circle-dashboard', component: CircleDashboard, meta: { title: 'Your CHDS Circle dashboard' } },
  { path: '/circle/profile', name: 'circle-profile', component: CircleProfile, meta: { title: 'CHDS Circle profile' } },
  { path: '/circle/schedule', name: 'circle-schedule', component: CircleLegal, meta: { title: 'CHDS Circle Program Schedule' } },
  { path: '/circle/legal/:slug', name: 'circle-legal', component: CircleLegal, meta: { title: 'CHDS Circle legal' } },
  // Partner co-branded ordering pages: the weekly flow (menu → packages → checkout → confirmation)
  // inside the partner's branding, with their venue as the pick-up point. PartnerShell loads
  // the partner first; a partner without an ordering page falls back to the classic landing.
  {
    path: '/partner/:slug',
    component: () => import('@/views/partner/PartnerShell.vue'),
    children: [
      { path: '', name: 'partner-menu', component: () => import('@/views/WeeklyMenu.vue'), meta: { title: 'Member meals — CHDS', partner: true } },
      { path: 'packages', name: 'partner-packages', component: () => import('@/views/Packages.vue'), meta: { title: 'Meal packages — CHDS', partner: true } },
      { path: 'checkout', name: 'partner-checkout', component: () => import('@/views/WeeklyCheckout.vue'), meta: { title: 'Checkout — CHDS', partner: true } },
      { path: 'order/:id', name: 'partner-order', component: () => import('@/views/WeeklyOrderSuccess.vue'), meta: { title: 'Order confirmed — CHDS', partner: true } },
    ],
  },
  {
    path: '/dashboard',
    component: DashboardLayout,
    meta: { requiresStaff: true, title: 'CHDS Operations' },
    children: [
      {
        path: '',
        // Drivers have no access to Overview (revenue) — land them on their run.
        redirect: () => ({ name: ROLE_HOME[currentRole()] || 'dash-overview' }),
      },
      {
        path: 'overview',
        name: 'dash-overview',
        component: Overview,
        meta: { title: 'Overview - CHDS Operations' },
      },
      {
        path: 'today',
        name: 'dash-today',
        component: TodayBoard,
        meta: { title: 'Today - CHDS Operations' },
      },
      {
        path: 'new',
        name: 'dash-new',
        component: NewOrder,
        meta: { title: 'New order - CHDS Operations' },
      },
      {
        path: 'orders',
        name: 'dash-orders',
        component: OrdersView,
        meta: { title: 'Orders - CHDS Operations' },
      },
      {
        path: 'menu',
        name: 'dash-menu',
        component: ProductsView,
        meta: { title: 'Products - CHDS Operations' },
      },
      {
        path: 'subscriptions',
        name: 'dash-subscriptions',
        component: () => import('@/views/dashboard/Subscriptions.vue'),
        meta: { title: 'Subscriptions - CHDS Operations' },
      },
      {
        path: 'weekly-menu',
        name: 'dash-weekly-menu',
        component: () => import('@/views/dashboard/WeeklyMenuPlanner.vue'),
        meta: { title: 'Weekly menu - CHDS Operations' },
      },
      {
        path: 'find-my-meals',
        name: 'dash-find-my-meals',
        component: () => import('@/views/dashboard/FindMyMeals.vue'),
        meta: { title: 'Find My Meals - CHDS Operations' },
      },
      {
        path: 'meal-matching',
        name: 'dash-meal-matching',
        component: () => import('@/views/dashboard/MealMatching.vue'),
        meta: { title: 'Meal matching - CHDS Operations' },
      },
      {
        path: 'products/:id',
        name: 'dash-product',
        component: ProductEditor,
        meta: { title: 'Edit product - CHDS Operations' },
      },
      {
        path: 'kitchen',
        name: 'dash-kitchen',
        component: KitchenView,
        meta: { title: 'Kitchen - CHDS Operations' },
      },
      {
        path: 'analytics',
        name: 'dash-analytics',
        component: AnalyticsView,
        meta: { title: 'Analytics - CHDS Operations' },
      },
      {
        path: 'traffic',
        name: 'dash-traffic',
        component: TrafficView,
        meta: { title: 'Traffic - CHDS Operations' },
      },
      {
        path: 'integrations',
        name: 'dash-integrations',
        component: IntegrationsView,
        meta: { title: 'Marketing integrations - CHDS Operations' },
      },
      {
        path: 'settings',
        name: 'dash-settings',
        component: SettingsView,
        meta: { title: 'Settings - CHDS Operations' },
      },
      {
        path: 'deliveries',
        name: 'dash-deliveries',
        component: DeliveriesView,
        meta: { title: 'Deliveries - CHDS Operations' },
      },
      {
        path: 'surveys',
        name: 'dash-surveys',
        component: Surveys,
        meta: { title: 'Lost-customer survey - CHDS Operations' },
      },
      {
        path: 'customers',
        name: 'dash-customers',
        component: CustomersView,
        meta: { title: 'Customers - CHDS Operations' },
      },
      {
        path: 'acquisition',
        name: 'dash-acquisition',
        component: AcquisitionView,
        meta: { title: 'Acquisition - CHDS Operations' },
      },
      {
        path: 'retention',
        name: 'dash-retention',
        component: RetentionView,
        meta: { title: 'Retention - CHDS Operations' },
      },
      {
        path: 'winback',
        name: 'dash-winback',
        component: WinbackView,
        meta: { title: 'Win-back - CHDS Operations' },
      },
      {
        path: 'ai-connector',
        name: 'dash-ai-connector',
        component: () => import('@/views/dashboard/AiConnector.vue'),
        meta: { title: 'AI connector - CHDS Operations' },
      },
      {
        path: 'checkout-problems',
        name: 'dash-checkout-problems',
        component: () => import('@/views/dashboard/CheckoutProblems.vue'),
        meta: { title: 'Checkout problems - CHDS Operations' },
      },
      {
        path: 'invoices',
        name: 'dash-invoices',
        component: InvoicesView,
        meta: { title: 'Invoices - CHDS Operations' },
      },
      {
        path: 'marketing',
        name: 'dash-marketing',
        component: MarketingView,
        meta: { title: 'Marketing - CHDS Operations' },
      },
      {
        path: 'dispatch',
        name: 'dash-dispatch',
        component: DispatchView,
        meta: { title: 'Dispatch - CHDS Operations' },
      },
      {
        path: 'circle',
        name: 'dash-circle',
        component: CircleAdmin,
        meta: { title: 'CHDS Circle - CHDS Operations' },
      },
      {
        path: 'sms',
        name: 'dash-sms',
        component: SmsCentre,
        meta: { title: 'SMS - CHDS Operations' },
      },
      {
        path: 'finance',
        name: 'dash-finance',
        component: FinanceView,
        meta: { title: 'Finance - CHDS Operations' },
      },
      {
        path: 'wallets',
        name: 'dash-wallets',
        component: WalletsView,
        meta: { title: 'Wallets - CHDS Operations' },
      },
      {
        path: 'support',
        name: 'dash-support',
        component: SupportView,
        meta: { title: 'Support - CHDS Operations' },
      },
      {
        path: 'translations',
        name: 'dash-translations',
        component: TranslationsView,
        meta: { title: 'Translations - CHDS Operations' },
      },
      {
        path: 'automations',
        name: 'dash-automations',
        component: AutomationsView,
        meta: { title: 'Email & automations - CHDS Operations' },
      },
      {
        path: 'seo',
        name: 'dash-seo',
        component: SeoManager,
        meta: { title: 'SEO - CHDS Operations' },
      },
      {
        path: 'parser-catalogue',
        name: 'dash-parser-catalogue',
        component: ParserCatalogue,
        meta: { title: 'Parser dishes - CHDS Operations' },
      },
      {
        path: 'meal-boxes',
        name: 'dash-meal-boxes',
        component: BoxManager,
        meta: { title: 'Meal Boxes page - CHDS Operations' },
      },
      {
        path: 'dish-info',
        name: 'dash-dish-info',
        component: DishInfoLibrary,
        meta: { title: 'Dish information - CHDS Operations' },
      },
      {
        path: 'operations',
        name: 'dash-operations',
        component: OpsSettings,
        meta: { title: 'Operations settings - CHDS Operations' },
      },
      {
        path: 'ai-chatbot',
        name: 'dash-ai-chatbot',
        component: ChatbotManager,
        meta: { title: 'AI Chatbot - CHDS Operations' },
      },
      {
        path: 'feedback',
        name: 'dash-feedback',
        component: FeedbackView,
        meta: { title: 'Feedback - CHDS Operations' },
      },
      {
        path: 'activity',
        name: 'dash-activity',
        component: ActivityView,
        meta: { title: 'Activity log - CHDS Operations' },
      },
      {
        path: 'my-notifications',
        name: 'dash-my-notifications',
        component: MyNotificationsView,
        meta: { title: 'My notifications - CHDS Operations' },
      },
      {
        path: 'help',
        name: 'dash-help',
        component: KnowledgeBase,
        meta: { title: 'Knowledge base - CHDS Operations' },
      },
    ],
  },
  { path: '/:pathMatch(.*)*', name: 'not-found', component: () => import('@/views/NotFound.vue'), meta: { title: 'Page not found — CHDS' } },
];

// Chinese URLs: every public page also lives at /zh/<path>, served in 中文, so the
// Chinese content has its own address for Google (hreflang pairs point both ways).
export const ZH_PATHS = ['/', '/ordernow', '/menu', '/packages', '/box', '/find-my-meals', '/ourstory',
  '/contact-us', '/privacy', '/terms', '/refund-policy', '/dish/:slug', '/guides', '/guides/:gslug',
  '/checkout-week', '/checkout', '/order/group/:id', '/order/success', '/profile', '/my-subscriptions'];
export function zhPath(path) { return path === '/' ? '/zh' : `/zh${path}`; }
export function enPath(path) { return path === '/zh' ? '/' : path.replace(/^\/zh(?=\/)/, ''); }
routes.filter((r) => ZH_PATHS.includes(r.path)).forEach((r) => {
  routes.push({ ...r, path: zhPath(r.path), name: `${r.name || r.path}-zh`, meta: { ...(r.meta || {}), lang: 'zh' } });
});
// keep the catch-all last
const _nf = routes.findIndex((r) => r.path === '/:pathMatch(.*)*');
if (_nf !== -1) routes.push(routes.splice(_nf, 1)[0]);

const router = createRouter({
  history: createWebHistory(),
  routes,
  // eslint-disable-next-line no-unused-vars
  scrollBehavior(to, from, savedPosition) {
    // Always scroll to top when navigating to a new route
    return { top: 0 };
  },
});

// Gate the operations dashboard to staff roles.
// When weekly menus are on (or a staff member is previewing them), every link into the
// legacy ordering pages lands on the new ones — Our Story, homepage hero, footer, chatbot.
const LEGACY_TO_WEEKLY = { '/box': '/packages', '/ordernow': '/menu', '/checkout': '/checkout-week' };
// A deploy renames the hashed chunks; a tab opened before it can't lazy-load the new route.
// Reload once onto the target URL instead of leaving a blank page.
router.onError((err, to) => {
  const msg = String((err && err.message) || err);
  if (/Loading (CSS )?chunk|ChunkLoadError|Failed to fetch dynamically imported module|Importing a module script failed/i.test(msg)) {
    const key = 'chds_chunk_reload';
    try {
      if (sessionStorage.getItem(key) !== (to && to.fullPath)) {
        sessionStorage.setItem(key, to && to.fullPath);
        window.location.assign((to && to.fullPath) || window.location.href);
      }
    } catch (e) { window.location.reload(); }
  }
});

router.beforeEach(async (to) => {
  // /zh/… pages always render in Chinese; leaving them keeps whatever the visitor chose.
  if (to.meta && to.meta.lang === 'zh') {
    try { const tr = useTranslationStore(); if (tr.currentLanguage !== 'zh') tr.setLanguage('zh'); } catch (e) { /* noop */ }
  }
  // Leaving a partner's co-branded page: drop its context BEFORE the weekly switch is read,
  // so a partner-enabled config never redirects the public /box → /packages.
  if (!String(to.path || '').startsWith('/partner/')) {
    try { const w = useWeeklyMenuStore(); if (w.partner) w.leavePartner(); } catch (e) { /* noop */ }
  }
  const target = LEGACY_TO_WEEKLY[enPath(to.path)];
  if (target && to.meta && to.meta.lang === 'zh') {
    const weekly = useWeeklyMenuStore();
    if (!weekly.config) { try { await weekly.loadConfig('zh'); } catch (e) { /* noop */ } }
    if (weekly.enabled) return { path: zhPath(target), query: to.query };
  }
  if (target) {
    const weekly = useWeeklyMenuStore();
    if (!weekly.config) { try { await weekly.loadConfig(); } catch (e) { /* stay on the old page */ } }
    if (weekly.enabled) return { path: target, query: to.query };
  }
  if (!to.matched.some((r) => r.meta && r.meta.requiresStaff)) return true;

  const authStore = useAuthStore();
  let user = authStore.user;
  if (!user) {
    try { user = JSON.parse(localStorage.getItem('user')); } catch (e) { user = null; }
  }
  const token = authStore.accessToken || localStorage.getItem('accessToken');

  if (!token || !user || !user.is_staff_role) {
    return { name: 'Home' };
  }

  // A restricted role landing on a screen it cannot read would get a 403, then a
  // 401 refresh, then a logout — staff experience that as "the dashboard signs me
  // out". Send them to their own screen instead.
  const allowed = ROLE_ALLOWED[user.role];
  if (allowed && to.name && !allowed.includes(to.name)) {
    return { name: ROLE_HOME[user.role] };
  }
  return true;
});

export default router;
